import type { ReactNode } from 'react';
import { ArrowLeft, ExternalLink, History } from 'lucide-react';
import {
  formatAccuracy,
  formatDateTime,
  formatDistance,
  formatRelative,
  formatSpeed,
  haversineMetres,
  type Device,
  type LatLon,
} from '@findmy/shared';
import { useAddress } from '../api/queries';
import { googleDirections, osmDirections } from '../lib/links';
import { BatteryIndicator, DeviceBadges, DeviceTypeIcon } from './DeviceBits';
import { DeviceCommands } from './DeviceCommands';
import { markerState, markerStateClasses, sourceLabel, typeLabel } from '../lib/device';
import { Button } from './ui';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export function DeviceDetail({
  device,
  now,
  me,
  onBack,
  onHistory,
}: {
  device: Device;
  now: number;
  me: LatLon | null;
  onBack: () => void;
  onHistory: () => void;
}) {
  const location = device.lastLocation;
  const { data: address } = useAddress(location);
  const state = markerState(device, now);
  const platform = [device.platformInfo.os, device.platformInfo.osVersion, device.platformInfo.model]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label="Back to all devices"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-full text-white ${markerStateClasses[state]}`}
        >
          <DeviceTypeIcon type={device.type} className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="truncate font-semibold">{device.name}</h2>
          <p className="text-xs text-slate-500">{typeLabel[device.type]}</p>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="flex flex-wrap gap-1 empty:hidden">
          <DeviceBadges device={device} />
        </div>

        {location ? (
          <p className="text-sm">
            {address?.address
              ? `Near ${address.address}`
              : `${location.lat.toFixed(5)}, ${location.lon.toFixed(5)}`}
          </p>
        ) : (
          <p className="text-sm text-slate-500">This device hasn’t reported a location yet.</p>
        )}

        <DeviceCommands device={device} />
        <Button onClick={onHistory} className="w-full">
          <History className="h-4 w-4" aria-hidden />
          History
        </Button>

        {location && (
          <div className="flex gap-2 text-sm">
            <a
              href={osmDirections(location, me)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-700 hover:underline dark:text-sky-400"
            >
              Directions (OpenStreetMap) <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
            <a
              href={googleDirections(location, me)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-700 hover:underline dark:text-sky-400"
            >
              Google Maps <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </div>
        )}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Fact label="Last seen">
            {device.lastSeenAt ? (
              <span title={formatDateTime(device.lastSeenAt)}>{formatRelative(device.lastSeenAt, now)}</span>
            ) : (
              'Never'
            )}
          </Fact>
          <Fact label="Battery">
            {device.batteryPct === null ? (
              'Unknown'
            ) : (
              <BatteryIndicator pct={device.batteryPct} charging={device.isCharging} />
            )}
          </Fact>
          {location && (
            <>
              <Fact label="Location recorded">{formatDateTime(location.recordedAt)}</Fact>
              <Fact label="Accuracy">{formatAccuracy(location.accuracyM)}</Fact>
              <Fact label="Source">{sourceLabel[location.source]}</Fact>
              {location.speed !== null && location.speed > 0.5 && (
                <Fact label="Speed">{formatSpeed(location.speed)}</Fact>
              )}
              {me && <Fact label="Distance from you">{formatDistance(haversineMetres(me, location))}</Fact>}
              <Fact label="Coordinates">
                {location.lat.toFixed(5)}, {location.lon.toFixed(5)}
              </Fact>
            </>
          )}
          {platform && <Fact label="Platform">{platform}</Fact>}
          {device.agentVersion && <Fact label="Agent version">{device.agentVersion}</Fact>}
          <Fact label="Paired">{formatDateTime(device.createdAt).slice(0, 10)}</Fact>
        </dl>
      </div>
    </div>
  );
}
